import React, { useEffect, useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { Text } from '../Typography';
import PdfPreview from './PdfPreview';
import TemplateVarsForm from './TemplateVarsForm';
import useMetaMask from '../../contexts/useMetaMask';
import { useIpfsDocumentSigner } from './useIpfsDocumentSigner';
import { IpfsDocument, IpfsDocumentSignature } from './ipfsDocumentTypes';
import {
  Body,
  ControlsColumn,
  Details,
  DocumentLabel,
  HeaderSection,
  HeaderSubtitle,
  HeaderTitle,
  OutlinedButton,
  PreviewColumn,
  PrimaryButton,
  ProcessingButton,
  Section,
  SignatureHeading,
  SubmitSection,
} from './signerStyles';

interface IpfsDocumentSignerProps {
  documentId: number;
  /** Fires once, when the connected wallet's signature first appears on the document. */
  onSigned?: (document: IpfsDocument) => void;
}

const ErrorNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ backgroundColor: '#ffebee', padding: '12px 16px', borderRadius: '8px', mt: 2 }}>
    <Text small sx={{ color: '#c62828', wordBreak: 'break-word' }}>{children}</Text>
  </Box>
);

const truncateAddress = (addr: string) =>
  addr.length > 12 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr;

const formatTimestamp = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const SignerRow: React.FC<{
  address: string;
  signature: IpfsDocumentSignature | undefined;
}> = ({ address, signature }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      padding: '12px 16px',
      borderRadius: '12px',
      backgroundColor: signature ? '#f0fdf4' : '#f9f9f9',
      border: `1px solid ${signature ? '#bbf7d0' : '#eee'}`,
    }}
  >
    {signature ? (
      <CheckCircleOutlineIcon sx={{ color: '#16a34a', fontSize: 20, mt: '1px', flexShrink: 0 }} />
    ) : (
      <HourglassEmptyIcon sx={{ color: '#999', fontSize: 20, mt: '1px', flexShrink: 0 }} />
    )}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
      {signature ? (
        <>
          <Text small sx={{ fontWeight: 700, color: '#15803d', lineHeight: '20px' }}>
            {signature.stakeholderName}
          </Text>
          <Text small sx={{ color: '#555', fontSize: '12px', lineHeight: '18px' }}>
            {truncateAddress(address)} · {signature.stakeholderRole}
          </Text>
          <Text small sx={{ color: '#888', fontSize: '11px', lineHeight: '16px' }}>
            Signed {formatTimestamp(signature.signedAt)}
          </Text>
        </>
      ) : (
        <>
          <Text small sx={{ fontWeight: 600, color: '#555', lineHeight: '20px' }}>
            Awaiting signature
          </Text>
          <Text small sx={{ color: '#999', fontSize: '12px' }}>
            {truncateAddress(address)}
          </Text>
        </>
      )}
    </Box>
  </Box>
);

const IpfsDocumentSigner: React.FC<IpfsDocumentSignerProps> = ({ documentId, onSigned }) => {
  const {
    document,
    placeholders,
    previewUrl,
    downloadUrls,
    loadingDoc,
    loadingPlaceholders,
    loadingPreview,
    docError,
    varDrafts,
    setVarDraft,
    saveVarsLoading,
    saveVarsError,
    handleSaveVars,
    signLoading,
    signError,
    finalizeLoading,
    finalizeError,
    downloadLoading,
    downloadError,
    allSigned,
    allVarsFilled,
    handleSign,
    handleFinalize,
    handleDownload,
  } = useIpfsDocumentSigner({ documentId });

  const { currentAccount } = useMetaMask();

  const docType = document?.type.replace(/_/g, ' ') ?? `Document #${documentId}`;
  const isCompleted = document?.status === 'completed';

  const isCreator =
    !!currentAccount &&
    document?.creatorAddress?.toLowerCase() === currentAccount.toLowerCase();

  const isRequiredSigner =
    !!currentAccount &&
    !!document?.requiredSigners.some((addr) => addr.toLowerCase() === currentAccount.toLowerCase());

  const hasAlreadySigned =
    !!currentAccount &&
    !!document?.signatures.some(
      (s) => s.stakeholderWallet.toLowerCase() === currentAccount.toLowerCase()
    );

  // Auto-fetch download URLs as soon as the document is completed.
  const downloadFetchedRef = useRef(false);
  useEffect(() => {
    if (isCompleted && !downloadUrls && !downloadLoading && !downloadFetchedRef.current) {
      downloadFetchedRef.current = true;
      void handleDownload();
    }
  }, [isCompleted, downloadUrls, downloadLoading, handleDownload]);

  // Notify the parent once, the first time the connected wallet's signature lands.
  const signaledRef = useRef(false);
  useEffect(() => {
    if (!onSigned || signaledRef.current) {return;}
    if (hasAlreadySigned && document) {
      signaledRef.current = true;
      onSigned(document);
    }
  }, [hasAlreadySigned, document, onSigned]);

  // Only the creator or a required signer may fill template variables, and only before finalization.
  const canEditVars = !isCompleted && (isCreator || isRequiredSigner);

  const templateVarsSection = document && (placeholders.length > 0 || loadingPlaceholders) && (
    <TemplateVarsForm
      heading="Document Details"
      description={
        canEditVars
          ? 'Fill in the fields you are responsible for. Fields already completed by another party are locked.'
          : 'Values supplied by the signatories for this document.'
      }
      placeholders={placeholders}
      values={varDrafts}
      templateVars={document.templateVars}
      owners={document.templateVarOwners}
      currentAccount={currentAccount}
      canEdit={canEditVars}
      loading={loadingPlaceholders}
      onChange={setVarDraft}
      onSave={canEditVars ? handleSaveVars : undefined}
      saving={saveVarsLoading}
      saveError={saveVarsError}
    />
  );

  const signersSection = document && (
    <Section>
      <SignatureHeading>Required Signers</SignatureHeading>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {document.requiredSigners.map((addr) => {
          const sig = document.signatures.find(
            (s) => s.stakeholderWallet.toLowerCase() === addr.toLowerCase()
          );
          return <SignerRow key={addr} address={addr} signature={sig} />;
        })}
      </Box>
    </Section>
  );

  const signSection = !isCompleted && isRequiredSigner && !hasAlreadySigned && (
    <Section>
      <SignatureHeading>Add Your Signature</SignatureHeading>
      {signLoading ? (
        <ProcessingButton disabled variant="contained">
          <CircularProgress size={20} sx={{ color: 'white' }} />
        </ProcessingButton>
      ) : (
        <PrimaryButton onClick={handleSign} disabled={loadingDoc} variant="contained">
          Sign with Wallet
        </PrimaryButton>
      )}
      {signError && <ErrorNote>{signError}</ErrorNote>}
    </Section>
  );

  const finalizeSection = allSigned && !isCompleted && (
    <SubmitSection>
      <SignatureHeading>All signatures collected</SignatureHeading>
      {finalizeLoading ? (
        <ProcessingButton disabled variant="contained">
          <CircularProgress size={20} sx={{ color: 'white' }} />
        </ProcessingButton>
      ) : (
        <PrimaryButton onClick={handleFinalize} disabled={!allVarsFilled} variant="contained">
          Finalize Document
        </PrimaryButton>
      )}
      <Text small sx={{ color: '#666', mt: 2, textAlign: 'center', maxWidth: '420px' }}>
        {allVarsFilled
          ? 'Compiles the final PDF, pins it to IPFS, and uploads to GCS.'
          : 'Every document field must be filled before the document can be finalized.'}
      </Text>
      {finalizeError && <ErrorNote>{finalizeError}</ErrorNote>}
    </SubmitSection>
  );

  const downloadSection = isCompleted && (
    <SubmitSection>
      {downloadUrls ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', alignItems: 'center' }}>
          <OutlinedButton
            onClick={() => window.open(downloadUrls.gcsUrl, '_blank', 'noopener,noreferrer')}
            variant="outlined"
          >
            Download from GCS
          </OutlinedButton>
          <Text small sx={{ color: '#888', fontSize: '11px', textAlign: 'center' }}>
            GCS link expires after 5 minutes. Re-fetch for a fresh URL.
          </Text>
        </Box>
      ) : (
        <ProcessingButton disabled variant="contained">
          <CircularProgress size={20} sx={{ color: 'white' }} />
        </ProcessingButton>
      )}
      {downloadError && <ErrorNote>{downloadError}</ErrorNote>}
    </SubmitSection>
  );

  if (loadingDoc) {
    return (
      <Details>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <CircularProgress />
        </Box>
      </Details>
    );
  }

  if (docError) {
    return (
      <Details>
        <ErrorNote>{docError}</ErrorNote>
      </Details>
    );
  }

  return (
    <Details>
      <HeaderSection>
        <HeaderTitle>{docType}</HeaderTitle>
        <HeaderSubtitle>
          {isCompleted
            ? 'Document finalized — all signatures collected and PDF stored on IPFS and GCS.'
            : 'Review the document below, then sign it with your connected wallet.'}
        </HeaderSubtitle>
      </HeaderSection>

      <Body horizontal={false}>
        <PreviewColumn>
          <DocumentLabel>
            <span className="prefix">IPFS Document #{documentId}</span>
            <span>{` : ${docType}`}</span>
          </DocumentLabel>
          {loadingPreview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <PdfPreview fileUrl={previewUrl} />
          )}
        </PreviewColumn>

        <ControlsColumn>
          {templateVarsSection}
          {signersSection}
          {signSection}
          {finalizeSection}
          {downloadSection}
        </ControlsColumn>
      </Body>
    </Details>
  );
};

export default IpfsDocumentSigner;
